import { useParams, Link } from "react-router-dom";
import BlogHeader from "../components/BlogHeader";
import BlogCTA from "../components/BlogCTA";
import SEO from "../components/SEO";
import { getPostBySlug, blogPosts } from "../data/blogPosts";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, ArrowLeft, User, Share2 } from "lucide-react";
import BlogCard from "../components/BlogCard";
import SocialLinks from "../components/SocialLinks";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug || "");

  if (!post) {
    return (
      <div className="blog-scope">
        <BlogHeader />
        <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
          <h1 className="font-display text-3xl font-bold text-foreground mb-4">Artigo não encontrado</h1>
          <Link to="/blog" className="text-primary hover:underline">← Voltar ao blog</Link>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(post.publishDate).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const related = blogPosts
    .filter((p) => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.publishDate,
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "Ataric",
      url: "https://www.ataric.com.br",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.ataric.com.br/blog/${post.slug}`,
    },
    keywords: post.tags.join(", "),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.ataric.com.br" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.ataric.com.br/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: `https://www.ataric.com.br/blog/${post.slug}` },
    ],
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const renderContent = (content: string) => {
    return content.split("\n").map((line, i) => {
      const imgMatch = line.match(/^!\[(.*?)\]\((.*?)\)$/);
      if (imgMatch) {
        return (
          <figure key={i} className="my-8">
            <img
              src={imgMatch[2]}
              alt={imgMatch[1]}
              className="w-full rounded-xl border border-border/50 shadow-lg shadow-primary/5"
              loading="lazy"
            />
            {imgMatch[1] && (
              <figcaption className="text-center text-xs text-muted-foreground mt-3 italic">{imgMatch[1]}</figcaption>
            )}
          </figure>
        );
      }
      if (line.startsWith("## ")) {
        return <h2 key={i} className="font-display text-2xl font-bold text-foreground mt-10 mb-4">{line.slice(3)}</h2>;
      }
      if (line.startsWith("### ")) {
        return <h3 key={i} className="font-display text-xl font-semibold text-foreground mt-8 mb-3">{line.slice(4)}</h3>;
      }
      if (line.startsWith("- ")) {
        const text = line.slice(2);
        return (
          <li key={i} className="text-muted-foreground ml-4 mb-2 list-disc marker:text-primary/60" dangerouslySetInnerHTML={{
            __html: text
              .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
              .replace(/\*(.*?)\*/g, '<em>$1</em>')
          }} />
        );
      }
      if (line.startsWith("| ")) return null;
      if (line.trim() === "") return <div key={i} className="h-4" />;
      if (/^\d+\.\s/.test(line)) {
        const text = line.replace(/^\d+\.\s/, "");
        return (
          <li key={i} className="text-muted-foreground ml-4 mb-2 list-decimal marker:text-primary/60" dangerouslySetInnerHTML={{
            __html: text
              .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
              .replace(/\*(.*?)\*/g, '<em>$1</em>')
          }} />
        );
      }
      return (
        <p key={i} className="text-muted-foreground leading-relaxed mb-3 text-[15px]" dangerouslySetInnerHTML={{
          __html: line
            .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
        }} />
      );
    });
  };

  return (
    <div className="blog-scope">
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        type="article"
        article={{
          publishedTime: post.publishDate,
          author: post.author,
          tags: post.tags,
        }}
        jsonLd={jsonLd}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <BlogHeader />

      <main className="container mx-auto max-w-3xl px-4 py-8">
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/blog" className="hover:text-primary transition-colors" itemProp="item">
                <span itemProp="name">Blog</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li className="text-border">/</li>
            <li className="text-foreground/60 truncate max-w-[200px]" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span itemProp="name">{post.title}</span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        <article>
          <header className="mb-10 relative">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-72 h-72 bg-primary/8 rounded-full blur-[80px] pointer-events-none" />
            <div className="relative">
              <div className="flex flex-wrap gap-2 mb-5">
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 font-medium">
                  {post.category}
                </Badge>
                {post.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs border-border/50 text-muted-foreground">
                    {tag}
                  </Badge>
                ))}
              </div>

              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground leading-tight mb-6">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground border-b border-border/50 pb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="flex items-center gap-1.5">
                    <User className="h-4 w-4 text-primary/60" /> {post.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-primary/60" /> {formattedDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-primary/60" /> {post.readTime} min de leitura
                  </span>
                </div>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Compartilhar artigo"
                >
                  <Share2 className="h-4 w-4" /> Compartilhar
                </button>
              </div>
            </div>
          </header>

          <div className="prose-custom">
            {renderContent(post.content)}
          </div>
        </article>

        <BlogCTA />

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground mb-6">Artigos Relacionados</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((p) => (
                <BlogCard key={p.id} post={p} />
              ))}
            </div>
          </section>
        )}

        <footer className="border-t border-border/50 pt-8 mt-12 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Ataric — Aprenda a Falar Inglês com IA</p>
          <SocialLinks />
        </footer>
      </main>
    </div>
  );
};

export default BlogPost;
